import React, { useState } from 'react';
import { AppLayout } from '@/components/AppLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { PageHeader } from '@/components/ui/page-header';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { 
  FileText, 
  Download, 
  Share, 
  Printer, 
  Eye,
  Upload,
  Save,
  FolderOpen,
  MessageCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate, useLocation } from 'react-router-dom';
import { FloatingTailor } from '@/components/FloatingTailor';
import { CollaborativeEditor } from '@/components/CollaborativeEditor';
import { supabase } from '@/integrations/supabase/client';
import { ResumeLibraryService, SavedResume } from '@/services/resumeLibraryService';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ResumeBuilder = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check for data passed from results page
  const passedData = location.state;
  const documentData = passedData?.documentData;
  const allDocuments = passedData?.allDocuments;
  const jobTitle = passedData?.jobTitle;
  const jobDescription = passedData?.jobDescription;
  const tailoringResults = passedData?.tailoringResults;

  const [selectedTemplate, setSelectedTemplate] = useState('template1');
  
  // Initialize document content from passed data or default
  const [documentContent, setDocumentContent] = useState(() => {
    if (documentData && documentData.content) {
      return typeof documentData.content === 'string' ? documentData.content : JSON.stringify(documentData.content);
    }
    return '';
  });

  const [activeDocument, setActiveDocument] = useState(() => {
    if (documentData) {
      return documentData.type === 'resume' ? 'resume' : documentData.type;
    }
    return 'resume';
  });

  const [showFloatingTailor, setShowFloatingTailor] = useState(true);
  const [lastAIChange, setLastAIChange] = useState<{request: string, summary: string, reasoning: string}>({request: '', summary: '', reasoning: ''});
  const [isChangesExpanded, setIsChangesExpanded] = useState(false);
  const [showLibraryDialog, setShowLibraryDialog] = useState(false);
  const [libraryResumes, setLibraryResumes] = useState<SavedResume[]>([]);
  const [isLoadingLibrary, setIsLoadingLibrary] = useState(false);

  const handleAIEdit = async (userRequest: string) => {
    if (!userRequest.trim()) {
      toast.error('Please enter a request for editing');
      return;
    }

    // Get current document content based on active tab
    let currentContent = '';
    
    if (activeDocument === 'resume') {
      currentContent = documentContent;
    } else if (activeDocument === 'cover-letter') {
      currentContent = allDocuments?.find(doc => doc.type === 'cover-letter')?.content || 
                       tailoringResults?.Cover_Letter || 
                       'No cover letter content available';
    } else if (activeDocument === 'linkedin-profile') {
      currentContent = allDocuments?.find(doc => doc.type === 'linkedin-profile')?.content || 
                       tailoringResults?.Linkedin_Profile || 
                       'No LinkedIn profile content available';
    } else if (activeDocument === 'thank-you-letter') {
      currentContent = allDocuments?.find(doc => doc.type === 'thank-you-letter')?.content || 
                       tailoringResults?.Thank_You_Letter || 
                       'No thank you letter content available';
    } else if (activeDocument === 'outreach-letter') {
      currentContent = allDocuments?.find(doc => doc.type === 'outreach-letter')?.content || 
                       tailoringResults?.Outreach_Letter || 
                       'No outreach letter content available';
    }

    if (!currentContent.trim()) {
      toast.error('No document content to edit');
      return;
    }

    try {
      toast.loading('AI is editing your document...', { id: 'ai-edit' });

      const { data, error } = await supabase.functions.invoke('ai-document-editor', {
        body: {
          userRequest,
          documentContent: currentContent,
          documentType: activeDocument
        }
      });

      if (error) {
        throw error;
      }

      if (data?.editedContent) {
        // Update the content based on the active document type
        if (activeDocument === 'resume') {
          // Handle both string and object responses from AI
          let editedContent = data.editedContent;
          
          // If AI returned an object, convert it back to string format
          if (typeof editedContent === 'object') {
            editedContent = `${editedContent.name || ''}\n${editedContent.title || ''} • ${editedContent.education || ''}\n${editedContent.location || ''} • ${editedContent.summary || ''}\n\n${editedContent.experience?.map((exp: any) => `${exp.title} ${exp.dates}\n• ${exp.responsibilities}`).join('\n\n') || ''}`;
          }
          
          setDocumentContent(editedContent);
        }
        // For other document types, we could update them if we had state for them
        // This would require adding state for other document types

        toast.success('Document successfully updated by AI!', { id: 'ai-edit' });
        setLastAIChange({
          request: userRequest,
          summary: data.changesSummary || `Applied user request: "${userRequest}"`,
          reasoning: data.changeReasoning || "Basic text modification based on user instructions."
        });
      } else {
        throw new Error('No edited content received from AI');
      }

    } catch (error) {
      console.error('AI edit error:', error);
      toast.error('Failed to edit document. Please try again.', { id: 'ai-edit' });
    }
  };

  const clearDocument = () => {
    setDocumentContent('');
    toast.success('Document cleared!');
  };

  const loadLibraryResumes = async () => {
    setIsLoadingLibrary(true);
    try {
      const resumes = await ResumeLibraryService.getSavedResumes();
      setLibraryResumes(resumes);
    } catch (error) {
      console.error('Error loading library resumes:', error);
      toast.error('Failed to load resume library');
    } finally {
      setIsLoadingLibrary(false);
    }
  };

  const handleOpenLibrary = async () => {
    setShowLibraryDialog(true);
    await loadLibraryResumes();
  };

  const handleLoadFromLibrary = (resume: SavedResume) => {
    setDocumentContent(resume.content);
    setShowLibraryDialog(false);
    toast.success(`Loaded "${resume.title}" from library`);
  };

  console.log('ResumeBuilder rendering...');

  return (
    <AppLayout currentPage="resume-builder">
      <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={documentData ? `Resume Tailoring Studio & Editor - ${documentData.title}` : "Resume Tailoring Studio & Editor"}
        description={documentData ? `Editing tailored document • ${jobTitle || 'Position'}` : "The World's Most Advanced Resume Tailoring Editor"}
        backTo="/"
      >
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/bulk-resume-generator')}
        >
          <FileText className="w-4 h-4 mr-2" />
          Bulk Generator
        </Button>
        <Button variant="outline" size="sm">
          <Upload className="w-4 h-4 mr-2" />
          Upload Resume
        </Button>
        <Button variant="outline" size="sm">
          <Save className="w-4 h-4 mr-2" />
          Save
        </Button>
        <Button variant="outline" size="sm">
          <Eye className="w-4 h-4 mr-2" />
          Preview
        </Button>
        <Button variant="outline" size="sm">
          <Download className="w-4 h-4 mr-2" />
          Download
        </Button>
        <Button variant="outline" size="sm">
          <Share className="w-4 h-4 mr-2" />
          Share
        </Button>
        <Button variant="outline" size="sm">
          <Printer className="w-4 h-4" />
        </Button>
      </PageHeader>

      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-12 gap-6 h-[calc(100vh-120px)]">
          {/* Left Sidebar - Template Selection */}
          <div className="col-span-2 space-y-4">
            <Card className="bg-gradient-surface border-card-border shadow-lg hover:shadow-xl transition-all duration-300">
              <CardContent className="p-6">
                <div className="space-y-6">
                  <div>
                    <Label className="text-sm font-bold mb-3 block text-foreground">Template Selection</Label>
                    <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
                      <SelectTrigger className="bg-gradient-secondary border-border-light shadow-sm hover:shadow-md transition-all duration-200">
                        <SelectValue placeholder="Choose template" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="template1">Professional</SelectItem>
                        <SelectItem value="template2">Modern</SelectItem>
                        <SelectItem value="template3">Creative</SelectItem>
                        <SelectItem value="template4">Executive</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label className="text-sm font-bold mb-3 block text-foreground">Document Management</Label>
                    <div className="space-y-2">
                      <Button 
                        onClick={handleOpenLibrary} 
                        variant="outline"
                        className="w-full text-xs"
                        size="sm"
                      >
                        <FolderOpen className="w-4 h-4 mr-2" />
                        Load from Library
                      </Button>
                      <Button 
                        onClick={clearDocument} 
                        variant="outline"
                        className="w-full text-destructive hover:bg-destructive hover:text-destructive-foreground text-xs"
                        size="sm"
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        Clear Document
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* What We Changed Panel */}
            <Card className={`bg-gradient-to-br from-warning-light via-warning-light/80 to-warning-light/60 border-warning/30 shadow-lg hover:shadow-xl transition-all duration-300 backdrop-blur-sm ${
              isChangesExpanded ? 'fixed inset-4 z-40 w-auto h-auto max-w-4xl max-h-[80vh] mx-auto my-auto overflow-auto' : ''
            }`}>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-bold text-sm text-foreground flex items-center gap-2">
                    <span className="w-2 h-2 bg-warning rounded-full animate-pulse"></span>
                    What we just changed and why we changed it?
                  </h3>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setIsChangesExpanded(!isChangesExpanded)}
                    className="h-6 w-6 p-0 hover:bg-white/20 rounded-full"
                  >
                    {isChangesExpanded ? '🗙' : '⛶'}
                  </Button>
                </div>
                <div className={`text-xs text-foreground/80 space-y-3 ${isChangesExpanded ? 'text-sm' : ''}`}>
                  {lastAIChange.request ? (
                    <>
                      <p className="flex items-start gap-3 p-2 bg-white/50 rounded-lg backdrop-blur-sm">
                        <span className="text-warning font-bold text-sm">📝</span>
                        <span className="font-medium"><strong>Your Request:</strong> "{lastAIChange.request}"</span>
                      </p>
                      <p className="flex items-start gap-3 p-2 bg-white/50 rounded-lg backdrop-blur-sm">
                        <span className="text-warning font-bold text-sm">🤖</span>
                        <span className="font-medium"><strong>What I Changed:</strong> {lastAIChange.summary}</span>
                      </p>
                      <p className="flex items-start gap-3 p-2 bg-white/50 rounded-lg backdrop-blur-sm">
                        <span className="text-warning font-bold text-sm">💡</span>
                        <span className="font-medium"><strong>Why I Changed It:</strong> {lastAIChange.reasoning}</span>
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="flex items-start gap-3 p-2 bg-white/50 rounded-lg backdrop-blur-sm">
                        <span className="text-warning font-bold text-sm">✓</span>
                        <span className="font-medium">Updated formatting for better readability and ATS optimization</span>
                      </p>
                      <p className="flex items-start gap-3 p-2 bg-white/50 rounded-lg backdrop-blur-sm">
                        <span className="text-warning font-bold text-sm">✓</span>
                        <span className="font-medium">Improved section organization and content flow</span>
                      </p>
                      <p className="flex items-start gap-3 p-2 bg-white/50 rounded-lg backdrop-blur-sm">
                        <span className="text-warning font-bold text-sm">✓</span>
                        <span className="font-medium">Enhanced keyword optimization for target role</span>
                      </p>
                      <p className="flex items-start gap-3 p-2 bg-white/50 rounded-lg backdrop-blur-sm">
                        <span className="text-warning font-bold text-sm">✓</span>
                        <span className="font-medium">Strengthened achievement statements with metrics</span>
                      </p>
                    </>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Center - Document Preview */}
          <div className="col-span-7">
            <div className="bg-background-secondary rounded-lg shadow-lg h-full overflow-hidden">
              {/* Document Tabs */}
              <Tabs value={activeDocument} onValueChange={setActiveDocument} className="h-full flex flex-col">
                <TabsList className="bg-card border-b border-border rounded-none h-14 justify-start px-4 gap-1">
                  <TabsTrigger 
                    value="resume" 
                    className="data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm border border-transparent data-[state=active]:border-border px-4 py-2 rounded-t-lg font-medium"
                  >
                    Resume
                  </TabsTrigger>
                  <TabsTrigger 
                    value="cover-letter" 
                    className="data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm border border-transparent data-[state=active]:border-border px-4 py-2 rounded-t-lg font-medium"
                  >
                    Cover Letter
                  </TabsTrigger>
                  <TabsTrigger 
                    value="linkedin-profile" 
                    className="data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm border border-transparent data-[state=active]:border-border px-4 py-2 rounded-t-lg font-medium"
                  >
                    LinkedIn
                  </TabsTrigger>
                  <TabsTrigger 
                    value="thank-you-letter" 
                    className="data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm border border-transparent data-[state=active]:border-border px-4 py-2 rounded-t-lg font-medium"
                  >
                    Thank You
                  </TabsTrigger>
                  <TabsTrigger 
                    value="outreach-letter" 
                    className="data-[state=active]:bg-white data-[state=active]:text-foreground data-[state=active]:shadow-sm border border-transparent data-[state=active]:border-border px-4 py-2 rounded-t-lg font-medium"
                  >
                    Outreach
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="resume" className="h-full p-0 m-0">
                  <div className="h-full">
                    {/* CKEditor Collaborative Document Editor */}
                    <div className="bg-gray-100 p-4 overflow-auto h-full">
                      {/* Document container - like MS Word's page view */}
                      <div className="max-w-[8.5in] mx-auto pb-8">
                        <div 
                          className="bg-white shadow-lg border border-gray-300 p-12 min-h-[11in] w-full max-w-[8.5in]"
                          style={{
                            minHeight: '11in',
                            margin: '0 auto',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1), 0 6px 20px rgba(0,0,0,0.1)'
                          }}
                        >
                          <CollaborativeEditor
                            value={documentContent}
                            onChange={setDocumentContent}
                            placeholder="Start collaborating on your resume..."
                            className="h-full"
                          />
                        </div>
                      </div>

                      {/* Status bar like MS Word */}
                      <div className="fixed bottom-0 left-0 right-0 bg-gray-200 border-t border-gray-300 px-4 py-1 text-xs text-gray-600 flex justify-between items-center z-10">
                        <div className="flex items-center gap-4">
                          <span>Page 1 of 1</span>
                          <span>{documentContent.replace(/<[^>]*>/g, '').split(/\s+/).filter(word => word.length > 0).length} words</span>
                          <span>{documentContent.replace(/<[^>]*>/g, '').length} characters</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span>100%</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="cover-letter" className="flex-1 m-0">
                  <div className="h-full bg-muted/30 overflow-y-auto">
                    <div className="flex justify-center py-8 px-4">
                      <div className="w-[8.5in] max-w-full bg-white shadow-xl min-h-[11in] p-8 relative">
                        <div className="font-serif text-[11pt] leading-relaxed">
                          {allDocuments?.find(doc => doc.type === 'cover-letter')?.content || 
                           tailoringResults?.Cover_Letter ||
                           'Cover letter content will appear here...'}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="linkedin-profile" className="flex-1 m-0">
                  <div className="h-full bg-muted/30 overflow-y-auto">
                    <div className="flex justify-center py-8 px-4">
                      <div className="w-[8.5in] max-w-full bg-white shadow-xl min-h-[11in] p-8 relative">
                        <div className="font-serif text-[11pt] leading-relaxed">
                          {allDocuments?.find(doc => doc.type === 'linkedin-profile')?.content || 
                           tailoringResults?.Linkedin_Profile ||
                           'LinkedIn profile content will appear here...'}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="thank-you-letter" className="flex-1 m-0">
                  <div className="h-full bg-muted/30 overflow-y-auto">
                    <div className="flex justify-center py-8 px-4">
                      <div className="w-[8.5in] max-w-full bg-white shadow-xl min-h-[11in] p-8 relative">
                        <div className="font-serif text-[11pt] leading-relaxed">
                          {allDocuments?.find(doc => doc.type === 'thank-you-letter')?.content || 
                           tailoringResults?.Thank_You_Letter ||
                           'Thank you letter content will appear here...'}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="outreach-letter" className="flex-1 m-0">
                  <div className="h-full bg-muted/30 overflow-y-auto">
                    <div className="flex justify-center py-8 px-4">
                      <div className="w-[8.5in] max-w-full bg-white shadow-xl min-h-[11in] p-8 relative">
                        <div className="font-serif text-[11pt] leading-relaxed">
                          {allDocuments?.find(doc => doc.type === 'outreach-letter')?.content || 
                           tailoringResults?.Outreach_Letter ||
                           'Outreach letter content will appear here...'}
                        </div>
                      </div>
                    </div>
                  </div>
                </TabsContent>
              </Tabs>
            </div>
          </div>

          {/* Right Sidebar - AI Assistant */}
          <div className="col-span-3">
            <Card className="shadow-xl border border-border bg-gradient-surface backdrop-blur-sm">
              <CardContent className="p-0 flex flex-col">
                <div className="p-6 bg-gradient-hero text-white rounded-t-lg relative overflow-hidden">
                  <div className="absolute inset-0 bg-white/10 backdrop-blur-sm"></div>
                  <div className="relative z-10 flex items-center gap-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm ring-2 ring-white/30 shadow-lg">
                      <span className="text-white text-xl">🧙‍♂️</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-xl">Hello, I'm Tailor!</h3>
                      <p className="text-sm opacity-90 leading-relaxed">Tell me what revisions you'd like to make to your new resume.</p>
                    </div>
                  </div>
                </div>

                <div className="flex-1 p-6 space-y-3 bg-gradient-secondary">
                  {/* AI Input Section */}
                  <div className="mb-4 p-4 border border-border rounded-lg shadow-sm bg-white/70 backdrop-blur-sm">
                    <div className="flex gap-3">
                      <Input 
                        placeholder="Tell me what changes you'd like me to make..."
                        className="flex-1 bg-white/90 border-border-light shadow-sm focus:shadow-md transition-all duration-200"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAIEdit(e.currentTarget.value);
                            e.currentTarget.value = '';
                          }
                        }}
                      />
                      <Button 
                        onClick={() => {
                          const input = document.querySelector('input[placeholder*="Tell me what changes"]') as HTMLInputElement;
                          if (input?.value) {
                            handleAIEdit(input.value);
                            input.value = '';
                          }
                        }}
                        className="bg-primary text-primary-foreground border-0 shadow-primary hover:shadow-lg transform hover:scale-[1.05] transition-all duration-300 px-4 hover:bg-primary-hover"
                      >
                        <MessageCircle className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2 text-center font-medium">
                      Press Enter or click the button to apply AI edits
                    </p>
                  </div>

                  <div className="space-y-3 border border-border rounded-lg p-4 shadow-lg bg-white/50 backdrop-blur-sm">
                    <Button className="w-full justify-start text-left h-auto py-4 px-5 bg-primary text-primary-foreground border-0 shadow-primary hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:bg-primary-hover text-xs">
                      <span>🔄 Auto Update My LinkedIn</span>
                    </Button>
                    <Button 
                      onClick={() => handleAIEdit('REMOVE_REDUNDANCIES')}
                      className="w-full justify-start text-left h-auto py-4 px-5 bg-success text-success-foreground border-0 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:brightness-110 text-xs"
                    >
                      <span>✂️ Remove Redundancies</span>
                    </Button>
                    <Button className="w-full justify-start text-left h-auto py-4 px-5 bg-accent text-accent-foreground border-0 shadow-accent hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:bg-accent-hover text-xs">
                      <span>🎨 Change Designs/Templates</span>
                    </Button>
                    <Button className="w-full justify-start text-left h-auto py-4 px-5 bg-info text-info-foreground border-0 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:brightness-110 text-xs">
                      <span>📝 Improve the Formatting</span>
                    </Button>
                    <Button className="w-full justify-start text-left h-auto py-4 px-5 bg-warning text-warning-foreground border-0 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:brightness-110 text-xs">
                      <span>🔧 Remove Skills Gaps</span>
                    </Button>
                    <Button className="w-full justify-start text-left h-auto py-4 px-5 bg-primary text-primary-foreground border-0 shadow-primary hover:shadow-lg transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:bg-primary-hover text-xs">
                      <span>🌟 Highlight Transferable Skills</span>
                    </Button>
                    <Button className="w-full justify-start text-left h-auto py-4 px-5 bg-success text-success-foreground border-0 shadow-lg hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:brightness-110 text-xs">
                      <span>📈 Optimize Work History</span>
                    </Button>
                    <Button className="w-full justify-start text-left h-auto py-4 px-5 bg-accent text-accent-foreground border-0 shadow-accent hover:shadow-xl transform hover:scale-[1.02] transition-all duration-300 font-semibold hover:bg-accent-hover text-xs">
                      <span>📊 Add Metrics & Numbers</span>
                    </Button>
                  </div>
                </div>

              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Floating Tailor Assistant */}
      {showFloatingTailor && (
        <FloatingTailor 
          onClose={() => setShowFloatingTailor(false)} 
          onAIEdit={handleAIEdit}
        />
      )}

      {/* Resume Library Dialog */}
      <Dialog open={showLibraryDialog} onOpenChange={setShowLibraryDialog}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Load Resume from Library</DialogTitle>
            <DialogDescription>
              Select a resume from your library to load into the editor
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {isLoadingLibrary ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading your resume library...</p>
              </div>
            ) : libraryResumes.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">No resumes found</h3>
                <p className="text-muted-foreground">
                  Upload and analyze resumes to build your library first.
                </p>
              </div>
            ) : (
              <div className="grid gap-4">
                {libraryResumes.map((resume) => (
                  <Card 
                    key={resume.id}
                    className="cursor-pointer transition-all hover:shadow-md hover:border-primary"
                    onClick={() => handleLoadFromLibrary(resume)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg border-2 border-muted flex items-center justify-center flex-shrink-0">
                          <FileText className="w-6 h-6 text-muted-foreground" />
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="font-semibold text-base mb-1">{resume.title}</h3>
                          <p className="text-sm text-muted-foreground mb-2">
                            Created: {new Date(resume.dateCreated).toLocaleDateString()}
                          </p>
                          {(resume.jobTitle || resume.companyName) && (
                            <div className="flex gap-2">
                              {resume.jobTitle && (
                                <span className="px-2 py-1 bg-primary/10 text-primary text-xs rounded">
                                  {resume.jobTitle}
                                </span>
                              )}
                              {resume.companyName && (
                                <span className="px-2 py-1 bg-secondary text-secondary-foreground text-xs rounded">
                                  {resume.companyName}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        
                        <Button variant="outline" size="sm">
                          Load Resume
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
    </AppLayout>
  );
};

export default ResumeBuilder;